// QRAdvancedOptions.jsx - Updated to use Context
import React, { useState } from 'react';
import styled from '@emotion/styled';
import { ChevronDown, Settings } from 'lucide-react';
import { useQRContext } from '../context/QRContext';

const AdvancedSection = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.12);
  }
`;

const AdvancedHeader = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }

  &:active {
    transform: scale(0.98);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const HeaderIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 8px;
  background: linear-gradient(45deg, #7c3aed, #ec4899);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
`;

const HeaderTitle = styled.span`
  font-size: 1rem;
  font-weight: 600;
`;

const HeaderSubtitle = styled.span`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 400;
`;

const ChevronIcon = styled.div`
  transition: transform 0.3s ease;
  transform: rotate(${({ expanded }) => (expanded ? '180deg' : '0deg')});
  color: rgba(255, 255, 255, 0.7);
`;

const AdvancedContent = styled.div`
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${({ expanded }) => (expanded ? '400px' : '0px')};
  opacity: ${({ expanded }) => (expanded ? '1' : '0')};
`;

const AdvancedInner = styled.div`
  padding: 0 1.5rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
`;

const OptionsCompactGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
`;

const InputGroup = styled.div`
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
`;

const Select = styled.select`
  width: 100%;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  padding: 0.625rem 0.75rem;
  color: white;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: #7c3aed;
    box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.1);
    background: rgba(255, 255, 255, 0.12);
  }

  &:hover {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
  }

  option {
    background: #1e293b;
    color: white;
    padding: 0.5rem;
  }
`;

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const RangeSlider = styled.input`
  flex: 1;
  height: 4px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 2px;
  outline: none;
  appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    background: #7c3aed;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.3);
    transition: all 0.3s ease;
  }

  &::-webkit-slider-thumb:hover {
    transform: scale(1.15);
    box-shadow: 0 3px 6px rgba(124, 58, 237, 0.4);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: #7c3aed;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(124, 58, 237, 0.3);
  }
`;

const SliderValue = styled.div`
  color: #7c3aed;
  font-weight: 600;
  min-width: 45px;
  text-align: center;
  font-size: 0.8rem;
`;

const CheckboxContainer = styled.div`
  grid-column: 1 / -1;
  margin-top: 0.5rem;
`;

const CheckboxGroup = styled.label`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.15);
  }
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  accent-color: #7c3aed;
  cursor: pointer;
`;

const CheckboxContent = styled.div`
  flex: 1;
`;

const CheckboxLabel = styled.span`
  color: rgba(255, 255, 255, 0.9);
  font-size: 0.875rem;
  font-weight: 500;
  display: block;
  margin-bottom: 0.125rem;
`;

const OptionDescription = styled.div`
  color: rgba(255, 255, 255, 0.6);
  font-size: 0.75rem;
  line-height: 1.4;
`;

const QRAdvancedOptions = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { qrOptions, setErrorCorrection, setCornerRadius, setIncludeMargin } =
    useQRContext();

  const handleErrorCorrectionChange = (e) => {
    setErrorCorrection(e.target.value);
  };

  const handleCornerRadiusChange = (e) => {
    setCornerRadius(parseInt(e.target.value));
  };

  const handleMarginChange = (e) => {
    setIncludeMargin(e.target.checked);
  };

  return (
    <AdvancedSection>
      <AdvancedHeader onClick={() => setIsExpanded(!isExpanded)}>
        <HeaderContent>
          <HeaderIcon>
            <Settings size={14} />
          </HeaderIcon>
          <HeaderText>
            <HeaderTitle>Advanced Options</HeaderTitle>
            <HeaderSubtitle>Error correction, margins & styling</HeaderSubtitle>
          </HeaderText>
        </HeaderContent>
        <ChevronIcon expanded={isExpanded}>
          <ChevronDown size={20} />
        </ChevronIcon>
      </AdvancedHeader>

      <AdvancedContent expanded={isExpanded}>
        <AdvancedInner>
          <OptionsCompactGrid>
            <InputGroup>
              <Label>Error Correction</Label>
              <Select
                value={qrOptions.errorCorrectionLevel}
                onChange={handleErrorCorrectionChange}
              >
                <option value="L">Low (7%)</option>
                <option value="M">Medium (15%)</option>
                <option value="Q">Quartile (25%)</option>
                <option value="H">High (30%)</option>
              </Select>
              <OptionDescription style={{ marginTop: '0.5rem' }}>
                Higher levels allow damaged QR codes to still scan
              </OptionDescription>
            </InputGroup>

            <InputGroup>
              <Label>Corner Radius</Label>
              <SliderContainer>
                <RangeSlider
                  type="range"
                  min="0"
                  max="20"
                  value={qrOptions.cornerRadius}
                  onChange={handleCornerRadiusChange}
                />
                <SliderValue>{qrOptions.cornerRadius}px</SliderValue>
              </SliderContainer>
              <OptionDescription style={{ marginTop: '0.5rem' }}>
                Rounds corners for a softer appearance
              </OptionDescription>
            </InputGroup>
          </OptionsCompactGrid>

          <CheckboxContainer>
            <CheckboxGroup>
              <Checkbox
                type="checkbox"
                checked={qrOptions.includeMargin}
                onChange={handleMarginChange}
              />
              <CheckboxContent>
                <CheckboxLabel>Include margin around QR code</CheckboxLabel>
                <OptionDescription>
                  Adds white space around the QR code for better scanning
                </OptionDescription>
              </CheckboxContent>
            </CheckboxGroup>
          </CheckboxContainer>
        </AdvancedInner>
      </AdvancedContent>
    </AdvancedSection>
  );
};

export default QRAdvancedOptions;
